import { computed, watch } from 'vue';
import { useAccount } from './useAccount';

/**
 * SICARG MOD 1 — White-label branding por cuenta.
 *
 * Lee la configuración de marca de la cuenta actual (company_name, primary_color,
 * logo_url, favicon_url) y la aplica dinámicamente al documento (título, favicon).
 * Si la cuenta no tiene branding configurado, no modifica los valores por defecto
 * de Chatwoot (fallback).
 */
export function useBranding() {
  const { currentAccount } = useAccount();

  const branding = computed(() => {
    const account = currentAccount.value || {};
    // El serializer puede entregar snake_case o camelCase según el pipeline
    return account.branding || account.branding_setting || {};
  });

  const companyName = computed(
    () => branding.value.company_name || branding.value.companyName || null
  );
  const primaryColor = computed(
    () => branding.value.primary_color || branding.value.primaryColor || null
  );
  const logoUrl = computed(
    () => branding.value.logo_url || branding.value.logoUrl || null
  );
  const faviconUrl = computed(
    () => branding.value.favicon_url || branding.value.faviconUrl || null
  );

  const applyFavicon = url => {
    if (!url) return;
    const links = document.querySelectorAll("link[rel*='icon']");
    if (links.length) {
      links.forEach(link => {
        link.setAttribute('href', url);
      });
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = url;
      document.head.appendChild(link);
    }
  };

  const applyTitle = name => {
    if (!name) return;
    document.title = name;
  };

  const applyBranding = () => {
    applyTitle(companyName.value);
    applyFavicon(faviconUrl.value);
  };

  // Aplicar automáticamente cuando el branding de la cuenta esté disponible
  watch(
    branding,
    () => {
      applyBranding();
    },
    { immediate: true, deep: true }
  );

  return {
    branding,
    companyName,
    primaryColor,
    logoUrl,
    faviconUrl,
    applyBranding,
  };
}
